// LIFF route shell.
//
// `/liff/*` is what every LIFF app on LINE Login channel 2009960133 lands
// on. Each Rich Menu cell is now a uri action pointing at a LIFF deep link
// whose endpoint URL matches a route below — see
// infra/line/rich-menu/endpoints.json for the canonical list.
//
// Routes are roughly grouped by area:
//   sites / incidents / events / reports — historical "picker" surfaces
//   manager / officer / patrol            — operational pages
//   incident/new + incident/my            — submission flows
//   admin/*                               — tenant_admin management
//   audit/*                               — auditor read-only
//   me + me/settings                      — personal pages (also fallback)
//   platform/*                            — super_admin platform pages
//
// IMPORTANT: every endpoint URL MUST mount inside a <LiffProvider> so
// liff.init() runs synchronously after page load. Otherwise the LINE
// in-app browser shows the generic "LIFF Load failed" overlay because it
// expects the SDK to call init within ~5 s.
//
// Each route picks its own `page` prop so LiffProvider initialises with
// the correct liffId. The page kinds mirror LiffPageKind in liff/types.ts
// AND the VALID_LIFF_APPS set in apps/auth-api/src/{liff-exchange,
// binding-redeem}.ts. Adding a route here without updating those will
// fail at /v1/auth/liff/exchange with `invalid_liff_app`.

import React from "react";
import { Routes, Route, useParams } from "react-router-dom";
import { LiffProvider, useLiff, RoleGate } from "../../liff/index.js";

// Legacy 6 page-kind LIFFs (kept as-is for back-compat — historical Rich
// Menus may still link to these specific picker / placeholder URLs).
import { SitePlaceholderPage } from "./pages/SitePlaceholder.js";
import { IncidentPlaceholderPage } from "./pages/IncidentPlaceholder.js";
import { EventPlaceholderPage } from "./pages/EventPlaceholder.js";
import { ManagerTodayPlaceholderPage } from "./pages/ManagerTodayPlaceholder.js";
import { OfficerTasksPlaceholderPage } from "./pages/OfficerTasksPlaceholder.js";
import { ReportPlaceholderPage } from "./pages/ReportPlaceholder.js";
import { SitePickerPage } from "./pages/SitePicker.js";
import { IncidentPickerPage } from "./pages/IncidentPicker.js";
import { EventPickerPage } from "./pages/EventPicker.js";
import { ReportPickerPage } from "./pages/ReportPicker.js";
import { BindingPage } from "./pages/Binding.js";

// New role-menu LIFF pages (21 endpoints — mirrors infra/line/rich-menu/endpoints.json).
import { PatrolTodayPage, PatrolLivePage, PatrolScanPage } from "./pages/PatrolPages.js";
import { ScanWizardPage } from "./pages/ScanWizardPage.js";
import { IncidentNewPage, IncidentMyPage } from "./pages/IncidentPages.js";
import { ReportsSitePage, ReportsAuditPage } from "./pages/ReportsPages.js";
import { ManagerSchedulePage, OfficerSchedulePage, OfficerHistoryPage } from "./pages/SchedulePages.js";
import { AuditPatrolPage, AuditLogPage } from "./pages/AuditPages.js";
import { AdminUsersPage, AdminInvitesPage } from "./pages/AdminPages.js";
import { MePage, MeSettingsPage } from "./pages/MePages.js";
import {
  PlatformTenantSwitcherPage, PlatformAnnouncementsPage, PlatformReportsPage,
  PlatformHealthPage, PlatformUsersPage,
} from "./pages/PlatformPages.js";

const ROLES_ALL_TENANT = ["tenant_admin", "manager", "officer", "incident_reporter", "auditor"] as const;
const ROLES_ALL_INCL_PLATFORM = [...ROLES_ALL_TENANT, "super_admin"] as const;

import { ConfirmDialogProvider } from "../../dashboard/components/ConfirmDialog.js";

export default function LiffShell(): React.ReactElement {
  return (
    <ConfirmDialogProvider>
    <Routes>
      {/* ── legacy 6 page-kind LIFFs ─────────────────────────────────── */}
      <Route path="sites"      element={<LiffProvider page="site_live"><RoleGate allow={["tenant_admin", "manager", "officer", "auditor"]}><SitePickerPage /></RoleGate></LiffProvider>} />
      <Route path="incidents"  element={<LiffProvider page="incident"><RoleGate allow={["tenant_admin", "manager", "officer", "incident_reporter", "auditor"]}><IncidentPickerPage /></RoleGate></LiffProvider>} />
      <Route path="events"     element={<LiffProvider page="event"><RoleGate allow={["tenant_admin", "manager", "auditor"]}><EventPickerPage /></RoleGate></LiffProvider>} />
      <Route path="reports"    element={<LiffProvider page="report"><RoleGate allow={["tenant_admin", "manager", "auditor"]}><ReportPickerPage /></RoleGate></LiffProvider>} />

      <Route path="sites/:siteId/live"     element={<LiffProvider page="site_live"><RoleGate allow={["tenant_admin", "manager", "officer", "auditor"]}><SitePlaceholderPage /></RoleGate></LiffProvider>} />
      <Route path="incidents/:incidentId"  element={<LiffProvider page="incident"><RoleGate allow={["tenant_admin", "manager", "officer", "incident_reporter", "auditor"]}><IncidentPlaceholderPage /></RoleGate></LiffProvider>} />
      <Route path="events/:eventId"        element={<LiffProvider page="event"><RoleGate allow={["tenant_admin", "manager", "auditor"]}><EventPlaceholderPage /></RoleGate></LiffProvider>} />
      <Route path="manager/today"          element={<LiffProvider page="manager_today"><RoleGate allow={["tenant_admin", "manager"]}><ManagerTodayPlaceholderPage /></RoleGate></LiffProvider>} />
      <Route path="officer/tasks"          element={<LiffProvider page="officer_tasks"><RoleGate allow={["officer", "tenant_admin"]}><OfficerTasksPlaceholderPage /></RoleGate></LiffProvider>} />
      <Route path="reports/:reportId"      element={<LiffProvider page="report"><RoleGate allow={["tenant_admin", "manager", "auditor"]}><ReportPlaceholderPage /></RoleGate></LiffProvider>} />

      {/* ── /liff/binding — guests redeem an 8-char code ────────────── */}
      <Route path="binding" element={<BindingPage />} />

      {/* ── patrol family ────────────────────────────────────────────── */}
      <Route path="patrol/today" element={<LiffProvider page="patrol_today"><RoleGate allow={["tenant_admin", "manager", "auditor"]}><PatrolTodayPage /></RoleGate></LiffProvider>} />
      <Route path="patrol/live"  element={<LiffProvider page="patrol_live"><RoleGate allow={["tenant_admin", "manager"]}><PatrolLivePage /></RoleGate></LiffProvider>} />
      <Route path="patrol/scan"  element={<LiffProvider page="patrol_scan"><RoleGate allow={["officer", "tenant_admin"]}><PatrolScanPage /></RoleGate></LiffProvider>} />
      <Route path="officer/scan/wizard" element={<LiffProvider page="patrol_scan"><RoleGate allow={["officer", "tenant_admin"]}><ScanWizardPage /></RoleGate></LiffProvider>} />

      {/* ── incident submission flows ────────────────────────────────── */}
      <Route path="incident/new" element={<LiffProvider page="incident_new"><RoleGate allow={["tenant_admin", "manager", "officer", "incident_reporter"]}><IncidentNewPage /></RoleGate></LiffProvider>} />
      <Route path="incident/my"  element={<LiffProvider page="incident_my"><RoleGate allow={["incident_reporter", "officer", "manager", "tenant_admin"]}><IncidentMyPage /></RoleGate></LiffProvider>} />

      {/* ── reports sub-routes ───────────────────────────────────────── */}
      <Route path="reports/site"  element={<LiffProvider page="reports_site"><RoleGate allow={["tenant_admin", "manager"]}><ReportsSitePage /></RoleGate></LiffProvider>} />
      <Route path="reports/audit" element={<LiffProvider page="reports_audit"><RoleGate allow={["tenant_admin", "auditor"]}><ReportsAuditPage /></RoleGate></LiffProvider>} />

      {/* ── schedule / history per role ──────────────────────────────── */}
      <Route path="manager/schedule"  element={<LiffProvider page="manager_schedule"><RoleGate allow={["tenant_admin", "manager"]}><ManagerSchedulePage /></RoleGate></LiffProvider>} />
      <Route path="officer/schedule"  element={<LiffProvider page="officer_schedule"><RoleGate allow={["officer", "tenant_admin"]}><OfficerSchedulePage /></RoleGate></LiffProvider>} />
      <Route path="officer/history"   element={<LiffProvider page="officer_history"><RoleGate allow={["officer", "tenant_admin"]}><OfficerHistoryPage /></RoleGate></LiffProvider>} />

      {/* ── auditor pages ────────────────────────────────────────────── */}
      <Route path="audit/patrol" element={<LiffProvider page="audit_patrol"><RoleGate allow={["auditor", "tenant_admin"]}><AuditPatrolPage /></RoleGate></LiffProvider>} />
      <Route path="audit/log"    element={<LiffProvider page="audit_log"><RoleGate allow={["auditor", "tenant_admin"]}><AuditLogPage /></RoleGate></LiffProvider>} />

      {/* ── tenant admin management ─────────────────────────────────── */}
      <Route path="admin/users"   element={<LiffProvider page="admin_users"><RoleGate allow={["tenant_admin"]}><AdminUsersPage /></RoleGate></LiffProvider>} />
      <Route path="admin/invites" element={<LiffProvider page="admin_invites"><RoleGate allow={["tenant_admin"]}><AdminInvitesPage /></RoleGate></LiffProvider>} />

      {/* ── me / settings (placeholder cells across all roles) ──────── */}
      <Route path="me"          element={<LiffProvider page="me"><RoleGate allow={[...ROLES_ALL_INCL_PLATFORM]}><MePage /></RoleGate></LiffProvider>} />
      <Route path="me/settings" element={<LiffProvider page="me_settings"><RoleGate allow={[...ROLES_ALL_INCL_PLATFORM]}><MeSettingsPage /></RoleGate></LiffProvider>} />

      {/* ── platform (super_admin) ──────────────────────────────────── */}
      <Route path="platform/tenant-switcher" element={<LiffProvider page="platform_tenant_switcher"><RoleGate allow={["super_admin"]}><PlatformTenantSwitcherPage /></RoleGate></LiffProvider>} />
      <Route path="platform/announcements"   element={<LiffProvider page="platform_announcements"><RoleGate allow={["super_admin"]}><PlatformAnnouncementsPage /></RoleGate></LiffProvider>} />
      <Route path="platform/reports"         element={<LiffProvider page="platform_reports"><RoleGate allow={["super_admin"]}><PlatformReportsPage /></RoleGate></LiffProvider>} />
      <Route path="platform/health"          element={<LiffProvider page="platform_health"><RoleGate allow={["super_admin"]}><PlatformHealthPage /></RoleGate></LiffProvider>} />
      <Route path="platform/users"           element={<LiffProvider page="platform_users"><RoleGate allow={["super_admin"]}><PlatformUsersPage /></RoleGate></LiffProvider>} />

      {/* Fallback for /liff/<unmatched> — render the /me page inside a
          LiffProvider so the SDK still inits. /me is role-agnostic so
          every authenticated user can see it. */}
      <Route path="*" element={<LiffProvider page="me"><RoleGate allow={[...ROLES_ALL_INCL_PLATFORM]}><MePage /></RoleGate></LiffProvider>} />
    </Routes>
    </ConfirmDialogProvider>
  );
}

// ── tiny "did init succeed?" debug panel for placeholder pages ──────────
export function LiffDebugPanel(): React.ReactElement {
  const { isReady, isAuthed, error, profile, session, refresh } = useLiff();
  return (
    <div className="rounded-xl border border-border bg-bg-surface p-4 mt-4 text-xs font-mono space-y-1">
      <div className="text-text-muted uppercase text-[10px] tracking-wider mb-2">系統資訊 (除錯用)</div>
      <Row k="isReady"  v={String(isReady)} />
      <Row k="isAuthed" v={String(isAuthed)} />
      {profile && <Row k="line_user_id" v={profile.userId.slice(0, 5) + "…" + profile.userId.slice(-3)} />}
      {profile && <Row k="display_name" v={profile.displayName} />}
      {session && <Row k="tenant_id" v={session.tenant_id} />}
      {session && <Row k="role" v={session.role} />}
      {session && <Row k="liff_app" v={session.liff_app} />}
      {session && <Row k="expires_at" v={session.expires_at} />}
      {error && <Row k="error" v={error} className="text-danger" />}
      <button
        onClick={refresh}
        className="mt-2 h-7 px-2 rounded bg-bg-raised text-[11px] text-text-secondary hover:bg-bg-hover"
      >
        重抓 session
      </button>
    </div>
  );
}

function Row({ k, v, className }: { k: string; v: string; className?: string }): React.ReactElement {
  return (
    <div className={`flex gap-2 ${className ?? ""}`}>
      <span className="text-text-muted shrink-0 w-24">{k}</span>
      <span className="text-text-secondary truncate">{v}</span>
    </div>
  );
}

// `useParams` re-export so placeholder pages can read route params without
// importing react-router directly.
export { useParams };
